import React, { useMemo } from "react"

import { useCurrentOverlay } from "shared/context/overlay/OverlayIDContext"
import { useEvent } from "shared/lib/useEvent"
import { ProductPosition } from "shared/types/ProductPosition"
import { ProjectChild } from "shared/types/Project"
import { Room } from "shared/types/Room"
import { ProductPositionAddon } from "shared/types/__generated__/graphql"
import { RowRendererProps } from "shared/ui/table/types"

import {
  MusterheftExportSelection,
  useMusterheftExport
} from "entities/projects/model/useMusterheftExport"
import { useProject } from "features/project-management/model/useProject"

export interface MusterheftExportOverlayModel {
  projectID: string
  projectChildren: ProjectChild[]
  isLoading: boolean
  onCancel: () => void
  onExport: (selection: MusterheftExportSelection) => void
  RowRenderer?: React.ComponentType<
    RowRendererProps<ProductPosition | ProductPositionAddon | Room>
  >
}

interface UseMusterheftExportOverlayModelArgs {
  projectID: string
}

/**
 * Model for the Musterheft export overlay opened on an owned project. Loads the
 * project's children via the owned-project endpoint. The shared-project variant
 * lives in useSharedMusterheftExportOverlayModel; the connector picks between
 * them so each calls exactly one data source.
 */
export function useMusterheftExportOverlayModel({
  projectID
}: UseMusterheftExportOverlayModelArgs): MusterheftExportOverlayModel {
  const { closeOverlay } = useCurrentOverlay()
  const { exportMusterheft } = useMusterheftExport(projectID)

  const projectModel = useProject({ projectID }, { includeChildren: true })

  const projectChildren = useMemo(
    () => projectModel.core.data.project?.data?.children ?? [],
    [projectModel.core.data.project?.data?.children]
  )

  const isLoading = !!projectModel.core.data.project?.isLoading

  const handleExport = useEvent((selection: MusterheftExportSelection) => {
    exportMusterheft(selection)
    closeOverlay()
  })

  return {
    projectID,
    projectChildren,
    isLoading,
    onCancel: closeOverlay,
    onExport: handleExport
  }
}
